/* eslint-disable @typescript-eslint/naming-convention */
// constants
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { ApiResponse, OrderApiData, GiftingOrderResponseData, TenancyInfoType } from "@swiggy-private/payment-core";

/**
 * Transforms the gifting order response into response understood by the
 * web payment client, similar to transformOrderResponse.
 *
 * @param response Gifting order response
 * @returns Transformed response with orderData
 */
export function transformGiftingOrderResponse(
    response?: ApiResponse<GiftingOrderResponseData, unknown>,
    rawResponse?: any,
): ApiResponse<OrderApiData, unknown> {
    if (!response || !response.data || response.statusCode !== API_SUCCESS_STATUS_CODE) {
        return (response as unknown) as ApiResponse<OrderApiData, unknown>;
    }

    const data = response.data;
    const orderTotal = data?.pricingInfo?.priceTotal?.finalPrice;
    const paymentMethod = data?.paymentInfo?.paymentMethod || "";
    const marketplaceReferenceId = data?.paymentInfo?.marketplaceReferenceId || "";
    const paymentRedirectionInfo = data?.paymentInfo?.paymentRedirectionInfo || "";
    const deliveryAddress = data?.deliveryAddress || null;

    // Extract all order items from business line order details
    const orderItems = [];
    for (let i = 0; i < data.blOrderDetails.length; i++) {
        const blOrder = data.blOrderDetails[i];
        const orderLines = Object.values(blOrder.orderLines);
        for (let j = 0; j < orderLines.length; j++) {
            const orderLine = orderLines[j];
            orderItems.push({
                quantity: orderLine.quantity,
            });
        }
    }

    // Determine order type based on business line details
    const orderType = data?.tenancyInfo?.businesslineId === TenancyInfoType.GIFTING ? "GIFTING" : "";

    // Extract success message from presentation details
    const successMessageV2 = data?.presentationDetails?.tickScreenComms?.header || "";
    const successMessageInfoV2 = data?.presentationDetails?.tickScreenComms?.subHeader || "";

    const orderData = {
        orderId: data.orderId || "",
        orderType,
        orderTotal,
        orderItems,
        orderPaymentMethod: paymentMethod,
        successMessageV2,
        successMessageInfoV2,
        appRedirectionInfo: paymentRedirectionInfo,
        paymentTxnId: marketplaceReferenceId,
        paymentPartnerOrderId: marketplaceReferenceId,
        targetedMarketplacePaymentReferenceId: marketplaceReferenceId,
        deliveryAddress,
        orderTransitionScreenNewMsgEnabled: true,
    };

    const responseToReturn = {
        statusCode: response.statusCode,
        statusMessage: response.statusMessage,
        data: {
            orderData,
        },
        rawResponse: {
            ...rawResponse,
            data: {
                ...rawResponse?.data,
                order_data: {
                    ...rawResponse?.data,
                },
            },
        },
    };

    return (responseToReturn as unknown) as ApiResponse<OrderApiData, unknown>;
}
